import { css, html, LitElement, nothing } from "lit";
import type { TemplateResult } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { customElement, property, state } from "lit/decorators.js";
import {
    TeqtankCommerce,
    type Commerce,
    type CommerceCart,
    type CommerceCartPurchaseType,
    type CommerceDataSource,
    type CommerceImage,
    type CommerceProduct,
    type CommerceProductComponent,
    type CommerceProductIdentity,
    type CommerceProductLookup,
    type CommerceProductVariant,
} from "@teqtank/commerce-sdk";
import "./teqtank-quick-buy.js";
import { sanitizeCommerceHtml } from "./sanitize-commerce-html.js";

export interface TeqtankProductAddedDetail {
    readonly product: CommerceProduct;
    readonly variant: CommerceProductVariant | null;
    readonly purchaseType: CommerceCartPurchaseType;
    readonly quantity: number;
    readonly cart: CommerceCart;
}

@customElement("teqtank-product-detail")
export class TeqtankProductDetail extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: false }) public accessor product: CommerceProduct | undefined;
    @property() public accessor identifier = "";
    @property() public accessor identity: CommerceProductIdentity | "" = "";
    @property({ attribute: "commerce-key" }) public accessor commerceKey = "demo";
    @property({ attribute: "data-source" }) public accessor dataSource: CommerceDataSource =
        "playground";
    @property({ attribute: "show-close", type: Boolean }) public accessor showClose = false;
    @property({ attribute: "show-quick-buy", type: Boolean }) public accessor showQuickBuy = true;
    @property({ attribute: "hide-quick-buy", type: Boolean }) public accessor hideQuickBuy = false;
    @property({ attribute: "mock-payment", type: Boolean }) public accessor mockPayment = false;

    @state() private accessor currentProduct: CommerceProduct | undefined;
    @state() private accessor selectedValues: Readonly<Record<string, string>> = Object.freeze({});
    @state() private accessor purchaseType: CommerceCartPurchaseType = "one-time";
    @state() private accessor quantity = 1;
    @state() private accessor loading = true;
    @state() private accessor adding = false;
    @state() private accessor errorMessage = "";
    @state() private accessor activeImageIndex = 0;
    @state() private accessor quickBuyOpen = false;
    @state() private accessor customerAuthenticated = false;

    private ownsCommerce = false;
    private loadSequence = 0;
    private unsubscribeCustomer: (() => void) | undefined;
    private imagePointerStartX: number | undefined;

    public static override styles = css`
        :host {
            container-type: inline-size;
            --tt-ink: var(--teqtank-color-text, #18221e);
            --tt-muted: var(--teqtank-color-text-muted, #69736d);
            --tt-accent: var(--teqtank-color-accent, #8d5c3f);
            --tt-soft: var(--teqtank-color-surface-soft, #f7f4ee);
            --tt-surface: var(--teqtank-color-surface, #ffffff);
            --tt-line: color-mix(in srgb, var(--tt-ink) 16%, transparent);
            display: block;
            color: var(--tt-ink);
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
        }

        * {
            box-sizing: border-box;
        }

        button,
        input {
            font: inherit;
        }

        button {
            cursor: pointer;
        }

        .loading,
        .empty,
        .error {
            padding: clamp(1.5rem, 5cqw, 4rem);
            border: 1px solid var(--tt-line);
            border-radius: 1.5rem;
            background: var(--tt-surface);
        }

        .error {
            color: #8b2424;
            background: #fff5f5;
        }

        .shell {
            position: relative;
            display: grid;
            grid-template-columns: var(
                --teqtank-product-detail-columns,
                minmax(0, 1.08fr) minmax(340px, 0.92fr)
            );
            gap: var(--teqtank-product-detail-gap, clamp(2rem, 5cqw, 5rem));
            align-items: start;
            padding: clamp(1rem, 3cqw, 2rem);
            background: var(--tt-surface);
        }

        .close {
            position: absolute;
            top: 0.5rem;
            right: 0.5rem;
            z-index: 2;
            width: 2.5rem;
            height: 2.5rem;
            border: 1px solid var(--tt-line);
            border-radius: 999px;
            background: color-mix(in srgb, var(--tt-surface) 92%, transparent);
            color: var(--tt-ink);
            font-size: 1.25rem;
        }

        .gallery {
            display: grid;
            gap: 0.8rem;
        }

        .primary-image {
            position: relative;
            min-height: clamp(320px, 54cqw, 680px);
            border-radius: var(
                --teqtank-product-detail-media-radius,
                var(--teqtank-radius-large, 1.75rem)
            );
            overflow: hidden;
            background:
                radial-gradient(
                    circle at 75% 15%,
                    color-mix(in srgb, var(--tt-accent) 20%, transparent),
                    transparent 38%
                ),
                var(--teqtank-product-detail-media-background, var(--tt-soft));
            display: grid;
            place-items: center;
            touch-action: pan-y;
            user-select: none;
        }

        .primary-image img {
            width: 100%;
            height: 100%;
            max-height: 680px;
            object-fit: var(--teqtank-product-image-fit, contain);
            display: block;
            -webkit-user-drag: none;
        }

        .image-fallback {
            display: grid;
            place-items: center;
            min-height: 320px;
            width: 100%;
            color: var(--tt-accent);
            font:
                600 clamp(2rem, 5cqw, 4.5rem)/1 Georgia,
                serif;
            text-align: center;
            padding: 2rem;
        }

        .thumbnails {
            display: flex;
            gap: 0.75rem;
            max-width: 100%;
            padding: 0.15rem 0.1rem 0.35rem;
            overflow-x: auto;
            overscroll-behavior-inline: contain;
            scrollbar-width: thin;
            scroll-snap-type: inline proximity;
        }

        .thumbnail {
            flex: 0 0 clamp(4.5rem, 9cqw, 5.5rem);
            aspect-ratio: 1;
            border: 1px solid var(--tt-line);
            border-radius: var(--teqtank-radius-medium, 0.85rem);
            overflow: hidden;
            padding: 0;
            background: var(--teqtank-product-detail-media-background, var(--tt-soft));
            box-shadow: 0 0.25rem 0.8rem color-mix(in srgb, var(--tt-ink) 8%, transparent);
            opacity: 0.76;
            scroll-snap-align: start;
            transition:
                border-color var(--teqtank-motion-fast, 120ms) ease,
                box-shadow var(--teqtank-motion-fast, 120ms) ease,
                opacity var(--teqtank-motion-fast, 120ms) ease,
                transform var(--teqtank-motion-fast, 120ms) ease;
        }

        .thumbnail:hover {
            opacity: 1;
            transform: translateY(-1px);
        }

        .thumbnail.active {
            border-color: var(--tt-accent);
            box-shadow:
                0 0 0 2px color-mix(in srgb, var(--tt-accent) 24%, transparent),
                0 0.35rem 1rem color-mix(in srgb, var(--tt-ink) 12%, transparent);
            opacity: 1;
        }

        .mobile-gallery-controls {
            display: none;
            grid-template-columns: 2.75rem 1fr 2.75rem;
            align-items: center;
            gap: 0.75rem;
        }

        .gallery-navigation {
            display: grid;
            width: 2.75rem;
            height: 2.75rem;
            place-items: center;
            border: 1px solid var(--tt-line);
            border-radius: 999px;
            background: var(--tt-surface);
            color: var(--tt-ink);
            font-size: 1.35rem;
            line-height: 1;
            box-shadow: 0 0.35rem 1rem color-mix(in srgb, var(--tt-ink) 10%, transparent);
        }

        .gallery-position {
            color: var(--tt-muted);
            font-size: 0.8rem;
            font-weight: 750;
            text-align: center;
        }

        .thumbnail:focus-visible {
            outline: 3px solid var(--teqtank-color-focus, var(--tt-accent));
            outline-offset: 2px;
        }

        .thumbnail img {
            width: 100%;
            height: 100%;
            object-fit: var(--teqtank-product-image-fit, contain);
        }

        .content {
            position: sticky;
            top: 1rem;
            padding: clamp(0.25rem, 2cqw, 1rem) 0;
        }

        .eyebrow {
            margin: 0 0 0.6rem;
            color: var(--tt-accent);
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.14em;
            text-transform: uppercase;
        }

        h1 {
            margin: 0;
            font:
                500 clamp(2.35rem, 5cqw, 4.6rem)/0.98 Georgia,
                serif;
            letter-spacing: -0.045em;
        }

        .short {
            margin: 1rem 0 0;
            color: var(--tt-muted);
            font-size: 1.02rem;
            line-height: 1.65;
        }

        .price-row {
            display: flex;
            flex-wrap: wrap;
            align-items: baseline;
            gap: 0.7rem;
            margin: 1.5rem 0 0.35rem;
        }

        .price {
            font-size: 1.55rem;
            font-weight: 800;
        }

        .retail {
            color: var(--tt-muted);
            text-decoration: line-through;
            font-size: 0.95rem;
        }

        .saving {
            color: var(--tt-accent);
            font-size: 0.78rem;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.07em;
        }

        .divider {
            border: 0;
            border-top: 1px solid var(--tt-line);
            margin: 1.6rem 0;
        }

        .purchase-options {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.75rem;
        }

        .purchase-option {
            display: grid;
            gap: 0.25rem;
            text-align: left;
            border: 1px solid var(--tt-line);
            border-radius: 1rem;
            padding: 0.95rem;
            background: var(--tt-surface);
            color: var(--tt-ink);
        }

        .purchase-option.active {
            border-color: var(--tt-accent);
            box-shadow: inset 0 0 0 1px var(--tt-accent);
            background: color-mix(in srgb, var(--tt-accent) 7%, var(--tt-surface));
        }

        .purchase-option strong {
            font-size: 0.92rem;
        }

        .purchase-option span {
            color: var(--tt-muted);
            font-size: 0.78rem;
        }

        .option-group {
            margin: 1.35rem 0 0;
            padding: 1.1rem 0 0;
            border: 0;
            border-top: 1px solid var(--tt-line);
        }

        .option-group legend {
            width: 100%;
            margin: 0 0 0.8rem;
            padding: 0;
            display: flex;
            align-items: center;
            gap: 0.55rem;
            font-size: 0.78rem;
            font-weight: 700;
        }

        .option-group legend > span {
            width: 1.4rem;
            height: 1.4rem;
            display: inline-grid;
            place-items: center;
            flex: 0 0 auto;
            border-radius: 50%;
            background: var(--tt-ink);
            color: white;
            font-size: 0.62rem;
        }

        .option-group legend strong {
            margin-left: auto;
            color: var(--tt-accent);
            font-weight: 700;
        }

        .option-values {
            display: flex;
            flex-wrap: wrap;
            gap: 0.55rem;
        }

        .option-value {
            border: 1px solid var(--tt-line);
            border-radius: 999px;
            padding: 0.62rem 0.9rem;
            background: var(--tt-surface);
            color: var(--tt-ink);
        }

        .option-value:hover,
        .option-value.active {
            border-color: var(--tt-ink);
            background: var(--tt-ink);
            color: white;
            transform: translateY(-1px);
        }

        .stock {
            margin: 0.7rem 0 0;
            color: var(--tt-muted);
            font-size: 0.78rem;
        }

        .actions {
            display: grid;
            grid-template-columns: auto minmax(0, 1fr);
            gap: 0.75rem;
            margin-top: 1.4rem;
        }

        .quantity {
            display: grid;
            grid-template-columns: 2.75rem 3rem 2.75rem;
            align-items: stretch;
            align-self: stretch;
            min-height: 2.75rem;
            border: 1px solid var(--tt-line);
            border-radius: var(
                --teqtank-product-detail-action-radius,
                var(--teqtank-radius-small, 0.9rem)
            );
            overflow: hidden;
        }

        .quantity button {
            display: grid;
            place-items: center;
            flex: 0 0 2.75rem;
            inline-size: 2.75rem;
            min-block-size: 2.75rem;
            padding: 0;
            border: 0;
            background: var(--tt-surface);
            color: var(--tt-ink);
            font: inherit;
            font-size: 1.15rem;
            line-height: 1;
            cursor: pointer;
        }

        .quantity button:hover:not(:disabled) {
            background: var(--tt-soft);
        }

        .quantity button:focus-visible,
        .quantity input:focus-visible {
            position: relative;
            z-index: 1;
            outline: 2px solid var(--teqtank-color-focus, var(--tt-accent));
            outline-offset: -2px;
        }

        .quantity button:disabled {
            cursor: not-allowed;
            opacity: 0.38;
        }

        .quantity input {
            flex: 0 0 3rem;
            inline-size: 3rem;
            min-block-size: 2.75rem;
            padding: 0;
            border: 0;
            border-inline: 1px solid var(--tt-line);
            text-align: center;
            background: var(--tt-surface);
            color: var(--tt-ink);
            font: inherit;
            font-weight: 700;
            appearance: textfield;
        }

        .quantity input::-webkit-inner-spin-button,
        .quantity input::-webkit-outer-spin-button {
            margin: 0;
            appearance: none;
        }

        .add {
            border: 1px solid var(--tt-accent);
            border-radius: var(
                --teqtank-product-detail-action-radius,
                var(--teqtank-radius-small, 0.9rem)
            );
            padding: 0.9rem 1.1rem;
            background: var(--tt-accent);
            color: white;
            font-weight: 800;
        }

        .add:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        .quick-buy {
            grid-column: 1 / -1;
            border: 1px solid var(--tt-ink);
            border-radius: 0.9rem;
            padding: 0.9rem 1.1rem;
            background: var(--tt-ink);
            color: white;
            font-weight: 800;
            cursor: pointer;
            box-shadow: 0 12px 28px color-mix(in srgb, var(--tt-ink) 18%, transparent);
            transition:
                transform 180ms ease,
                box-shadow 180ms ease;
        }

        .quick-buy:hover {
            transform: translateY(-2px);
            box-shadow: 0 18px 36px color-mix(in srgb, var(--tt-ink) 24%, transparent);
        }

        .description {
            margin-top: 1.6rem;
            color: var(--tt-muted);
            line-height: 1.7;
            white-space: pre-line;
        }

        .detail-sections {
            margin-top: 1.4rem;
            display: grid;
            gap: 1rem;
        }

        .detail-section {
            color: var(--tt-muted);
            line-height: 1.7;
        }

        .detail-section h2 {
            margin: 0 0 0.6rem;
            color: var(--tt-ink);
            font: 600 1.1rem/1.3 var(--teqtank-font-heading, Georgia, serif);
        }

        .detail-rich > :first-child {
            margin-top: 0;
        }
        .detail-rich > :last-child {
            margin-bottom: 0;
        }
        .detail-rich img {
            max-width: 100%;
            height: auto;
        }
        .detail-rich table {
            width: 100%;
            border-collapse: collapse;
        }
        .detail-rich th,
        .detail-rich td {
            padding: 0.45rem;
            border: 1px solid var(--tt-line);
            text-align: left;
        }
        .detail-rich a {
            color: var(--tt-accent);
        }

        .detail-accordion {
            border-block: 1px solid var(--tt-line);
        }

        .detail-accordion + .detail-accordion {
            border-top: 0;
        }
        .detail-accordion summary {
            padding: 0.85rem 0;
            cursor: pointer;
            font-weight: 800;
        }
        .detail-accordion .detail-rich,
        .detail-accordion .detail-plain {
            padding-bottom: 0.9rem;
        }
        .detail-plain {
            white-space: pre-line;
        }

        .kit-contents {
            margin-top: 1.25rem;
            border-block: 1px solid var(--tt-line);
            padding-block: 0.15rem;
        }

        .kit-contents summary {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            padding: 0.85rem 0;
            cursor: pointer;
            font-size: 0.82rem;
            font-weight: 800;
            list-style: none;
        }

        .kit-contents summary::-webkit-details-marker {
            display: none;
        }
        .kit-contents summary::after {
            content: "+";
            color: var(--tt-muted);
        }
        .kit-contents[open] summary::after {
            content: "−";
        }

        .component-list {
            margin: 0;
            padding: 0 0 0.85rem;
            display: grid;
            gap: 0.5rem;
            list-style: none;
        }

        .component-list .component-list {
            margin: 0.35rem 0 0 1.1rem;
            padding: 0;
        }

        .component {
            display: grid;
            grid-template-columns: 2rem minmax(0, 1fr) auto;
            align-items: center;
            gap: 0.65rem;
            min-width: 0;
            color: var(--tt-muted);
            font-size: 0.78rem;
        }

        .component img,
        .component-image-placeholder {
            width: 2rem;
            height: 2rem;
            border-radius: 0.45rem;
            object-fit: var(--teqtank-product-image-fit, contain);
            background: var(--tt-soft);
        }

        .component-name {
            overflow-wrap: anywhere;
        }
        .component-quantity {
            white-space: nowrap;
            font-weight: 700;
        }

        .tags {
            display: flex;
            flex-wrap: wrap;
            gap: 0.45rem;
            margin-top: 1.25rem;
        }

        .tag {
            border-radius: 999px;
            padding: 0.35rem 0.6rem;
            background: var(--tt-soft);
            color: var(--tt-muted);
            font-size: 0.7rem;
            font-weight: 700;
        }

        .inline-error {
            margin-top: 0.9rem;
            color: #8b2424;
            font-size: 0.82rem;
        }

        @container (max-width: 860px) {
            .shell {
                grid-template-columns: 1fr;
                gap: 1.25rem;
            }

            .content {
                position: static;
            }

            .primary-image {
                min-height: min(90cqw, 520px);
            }
        }

        @container (max-width: 520px) {
            .shell {
                padding: 0.75rem;
            }

            .primary-image {
                min-height: 0;
                aspect-ratio: 4 / 3;
                border-radius: 1.2rem;
            }

            .thumbnails {
                display: none;
            }

            .mobile-gallery-controls {
                display: grid;
            }

            .purchase-options,
            .actions {
                grid-template-columns: 1fr;
            }

            .quantity {
                width: max-content;
            }
        }
    `;

    protected override firstUpdated(): void {
        void this.bindCustomer();
    }

    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (
            changed.has("commerce") ||
            changed.has("product") ||
            changed.has("identifier") ||
            changed.has("identity")
        ) {
            void this.synchronizeProduct();
        }
    }

    public override disconnectedCallback(): void {
        this.loadSequence++;
        this.unsubscribeCustomer?.();
        this.unsubscribeCustomer = undefined;

        if (this.ownsCommerce) {
            this.commerce?.dispose();
        }

        super.disconnectedCallback();
    }

    public async load(
        identifier: string = this.identifier,
        identity?: CommerceProductIdentity,
    ): Promise<CommerceProduct> {
        const runtime = await this.requireCommerce();
        const lookup = this.resolveLookup(runtime, identifier, identity);

        if (lookup === null) {
            throw new Error(
                "A Product identifier could not be resolved from the component or current URL.",
            );
        }

        const product = await runtime.products.get(lookup.identifier, lookup.identity);
        this.applyProduct(product);
        this.dispatchLoaded(product);

        return product;
    }

    public async loadFromLocation(
        location: string | URL = globalThis.location.href,
    ): Promise<CommerceProduct> {
        const runtime = await this.requireCommerce();
        const lookup = runtime.products.resolveDetailLookup(location);

        if (lookup === null) {
            throw new Error("The current URL does not contain a Product identifier.");
        }

        return this.load(lookup.identifier, lookup.identity);
    }

    public async addToCart(): Promise<CommerceCart> {
        const product = this.currentProduct;

        if (product === undefined) {
            throw new Error("A Product must be loaded before it can be added to the Cart.");
        }

        const variant = this.resolveSelectedVariant(product);

        if (product.variants.length > 0 && variant === null) {
            throw new Error(
                "Select the required Product options before adding this Product to the Cart.",
            );
        }

        if (variant !== null && !variant.available) {
            throw new Error("The selected Product option is currently unavailable.");
        }

        const runtime = await this.requireCommerce();
        const offer = variant?.offers.find(
            (candidate) => candidate.purchaseType === this.purchaseType,
        );
        const frequency = offer?.frequency?.options.find(
            (candidate) => candidate.id === offer.frequency?.defaultOptionId,
        );
        const cart = await runtime.cart.add({
            productId: product.id,
            ...(variant === null ? {} : { variantId: variant.id }),
            quantity: this.quantity,
            purchaseType: this.purchaseType,
            ...(frequency === undefined
                ? {}
                : { frequencyId: frequency.id, frequencyDays: frequency.days }),
        });

        const detail: TeqtankProductAddedDetail = Object.freeze({
            product,
            variant,
            purchaseType: this.purchaseType,
            quantity: this.quantity,
            cart,
        });

        this.dispatchEvent(
            new CustomEvent<TeqtankProductAddedDetail>("teqtank-product-added", {
                detail,
                bubbles: true,
                composed: true,
            }),
        );

        return cart;
    }

    protected override render() {
        if (this.loading) {
            return html`<div class="loading" part="loading-state">Loading Product details…</div>`;
        }

        if (this.currentProduct === undefined) {
            return this.errorMessage.length > 0
                ? html`<div class="error" part="error-state" role="alert">
                      ${this.errorMessage}
                  </div>`
                : html`<div class="empty" part="empty-state">
                      Select a Product to view its details.
                  </div>`;
        }

        const product = this.currentProduct;
        const variant = this.resolveSelectedVariant(product);
        const offers = variant?.offers ?? product.representativeOffers;
        const oneTimeOffer = offers.find((offer) => offer.purchaseType === "one-time");
        const recurringOffer = offers.find((offer) => offer.purchaseType === "recurring");
        const activeOffer =
            offers.find((offer) => offer.purchaseType === this.purchaseType) ??
            oneTimeOffer ??
            offers[0];
        const activePrice = activeOffer?.price.current;
        const compareAt = activeOffer?.price.compareAt;
        const displayImages = this.displayImages(product);
        const activeImage =
            displayImages[this.activeImageIndex] ??
            this.validImage(product.primaryImage) ??
            displayImages[0] ??
            null;
        const canAdd =
            !this.adding &&
            product.status === "active" &&
            variant?.available === true &&
            activeOffer !== undefined;
        const activeOptionGroups = this.activeOptionGroups(product);
        const displaySku = variant?.displaySku ?? product.displaySku;

        return html`
            <article class="shell" part="product-detail shell">
                ${
                    this.showClose
                        ? html`<button
                              class="close"
                              part="close"
                              type="button"
                              aria-label="Close Product details"
                              @click=${this.requestClose}
                          >
                              ×
                          </button>`
                        : nothing
                }

                <section class="gallery" part="gallery" aria-label="Product images">
                    <div
                        class="primary-image"
                        part="primary-media"
                        @pointerdown=${this.productImagePointerDown}
                        @pointerup=${(event: PointerEvent) =>
                            this.productImagePointerUp(event, displayImages.length)}
                        @pointercancel=${this.cancelProductImageSwipe}
                    >
                        ${
                            activeImage === null
                                ? html`<div class="image-fallback">${product.name}</div>`
                                : html`<img
                                      src=${activeImage.url}
                                      alt=${activeImage.alternativeText}
                                      draggable="false"
                                      decoding="async"
                                  />`
                        }
                    </div>
                    ${
                        displayImages.length > 1
                            ? html`<div class="thumbnails" part="thumbnails">
                                  ${displayImages.map(
                                      (image, index) => html`
                                          <button
                                              class="thumbnail ${
                                                  index === this.activeImageIndex ? "active" : ""
                                              }"
                                              part="thumbnail"
                                              type="button"
                                              aria-label=${`View ${image.alternativeText.trim() || product.name} image ${index + 1}`}
                                              aria-pressed=${
                                                  index === this.activeImageIndex ? "true" : "false"
                                              }
                                              @click=${() => (this.activeImageIndex = index)}
                                          >
                                              <img src=${image.url} alt="" loading="lazy" />
                                          </button>
                                      `,
                                  )}
                              </div>`
                            : nothing
                    }
                    ${
                        displayImages.length > 1
                            ? html`<div
                                  class="mobile-gallery-controls"
                                  part="mobile-gallery-controls"
                                  aria-label="Product image controls"
                              >
                                  <button
                                      class="gallery-navigation"
                                      part="gallery-previous"
                                      type="button"
                                      aria-label="View previous product image"
                                      @click=${() => this.selectRelativeImage(-1, displayImages.length)}
                                  >
                                      ←
                                  </button>
                                  <span class="gallery-position" role="status" aria-live="polite">
                                      Image ${this.activeImageIndex + 1} of ${displayImages.length}
                                  </span>
                                  <button
                                      class="gallery-navigation"
                                      part="gallery-next"
                                      type="button"
                                      aria-label="View next product image"
                                      @click=${() => this.selectRelativeImage(1, displayImages.length)}
                                  >
                                      →
                                  </button>
                              </div>`
                            : nothing
                    }
                </section>

                <section class="content" part="content">
                    <p class="eyebrow" part="eyebrow">${product.tags[0] ?? "Product detail"}</p>
                    <h1 part="title">${product.name}</h1>
                    <p class="short" part="summary">${product.shortDescription}</p>

                    <div class="price-row" part="price-row">
                        <span class="price" part="price"
                            >${activePrice?.formatted ?? "Unavailable"}</span
                        >
                        ${compareAt === undefined ? nothing : html`<span class="retail">${compareAt.formatted}</span>`}
                        ${
                            this.purchaseType === "recurring" && recurringOffer !== undefined
                                ? html`<span class="saving">Subscribe & save</span>`
                                : nothing
                        }
                    </div>
                    ${
                        displaySku === undefined && variant?.inventoryQuantity === undefined
                            ? nothing
                            : html`<p class="stock">
                                  ${displaySku === undefined ? "" : `SKU ${displaySku}`}${variant?.inventoryQuantity === undefined ? "" : `${displaySku === undefined ? "" : " · "}${variant.inventoryQuantity} available`}
                              </p>`
                    }

                    <hr class="divider" />

                    ${
                        recurringOffer !== undefined
                            ? html`<div
                                  class="purchase-options"
                                  part="purchase-options"
                                  aria-label="Purchase type"
                              >
                                  <button
                                      class="purchase-option ${this.purchaseType === "one-time" ? "active" : ""}"
                                      type="button"
                                      @click=${() => this.selectPurchaseType("one-time")}
                                  >
                                      <strong>One-time purchase</strong>
                                      <span
                                          >${oneTimeOffer?.price.current.formatted ?? "Unavailable"}</span
                                      >
                                  </button>
                                  <button
                                      class="purchase-option ${this.purchaseType === "recurring" ? "active" : ""}"
                                      type="button"
                                      @click=${() => this.selectPurchaseType("recurring")}
                                  >
                                      <strong>Subscribe & save</strong>
                                      <span>${recurringOffer.price.current.formatted}</span>
                                  </button>
                              </div>`
                            : nothing
                    }
                    ${activeOptionGroups.map((option, index) => {
                        const values = this.availableValues(product, option.id);
                        const selected = values.find(
                            (value) => value.id === this.selectedValues[option.id],
                        );

                        return html`
                            <fieldset class="option-group" part="option-group">
                                <legend>
                                    <span>${index + 1}</span>
                                    Choose ${option.name.toLowerCase()}
                                    <strong>${selected?.label ?? ""}</strong>
                                </legend>
                                <div class="option-values" part="option-values">
                                    ${values.map(
                                        (value) => html`
                                            <button
                                                class="option-value ${this.selectedValues[option.id] === value.id ? "active" : ""}"
                                                part="option-value"
                                                type="button"
                                                @click=${() => this.selectOption(option.id, value.id)}
                                            >
                                                ${value.label}
                                            </button>
                                        `,
                                    )}
                                </div>
                            </fieldset>
                        `;
                    })}

                    <div class="actions" part="actions">
                        <div class="quantity" part="quantity" aria-label="Quantity">
                            <button
                                type="button"
                                aria-label="Decrease quantity"
                                ?disabled=${this.quantity <= 1}
                                @click=${this.decreaseQuantity}
                            >
                                −
                            </button>
                            <input
                                type="number"
                                inputmode="numeric"
                                min="1"
                                max="99"
                                .value=${String(this.quantity)}
                                @change=${this.changeQuantity}
                                aria-label="Quantity"
                            />
                            <button
                                type="button"
                                aria-label="Increase quantity"
                                ?disabled=${this.quantity >= 99}
                                @click=${this.increaseQuantity}
                            >
                                +
                            </button>
                        </div>
                        <button
                            class="add"
                            part="primary-action add-to-cart"
                            type="button"
                            ?disabled=${!canAdd}
                            @click=${this.handleAddToCart}
                        >
                            ${this.adding ? "Adding…" : `Add to bag${activePrice === undefined ? "" : ` · ${activePrice.formatted}`}`}
                        </button>
                        ${this.showQuickBuy && !this.hideQuickBuy && this.commerce?.context.features.has("quick-buy") === true && this.customerAuthenticated && (this.commerce.customer.customer?.addresses.length ?? 0) > 0 && (this.commerce.customer.customer?.savedPaymentMethods?.length ?? 0) > 0 ? html`<button class="quick-buy" part="quick-buy-action" type="button" ?disabled=${!canAdd} @click=${() => (this.quickBuyOpen = true)}>Quick buy${activePrice === undefined ? "" : ` · ${activePrice.formatted}`}</button>` : nothing}
                    </div>
                    ${this.quickBuyOpen ? html`<teqtank-quick-buy .commerce=${this.commerce} .product=${product} .variantId=${variant?.id ?? ""} .quantity=${this.quantity} .purchaseType=${this.purchaseType} .mockPayment=${this.mockPayment} @teqtank-quick-buy-close=${() => (this.quickBuyOpen = false)} @teqtank-quick-buy-complete=${() => (this.quickBuyOpen = false)}></teqtank-quick-buy>` : nothing}
                    ${this.errorMessage.length > 0 ? html`<p class="inline-error" part="error-state" role="alert">${this.errorMessage}</p>` : nothing}

                    <p class="description" part="description">${product.description}</p>
                    ${this.renderDetailSections(product)}
                    ${
                        product.productType === "kit" && (product.components?.length ?? 0) > 0
                            ? html`
                                  <details class="kit-contents" part="kit-contents">
                                      <summary>
                                          What's included
                                          <span>${product.components!.length} components</span>
                                      </summary>
                                      ${this.renderComponents(product.components!)}
                                  </details>
                              `
                            : nothing
                    }
                    ${
                        product.tags.length > 0
                            ? html`<div class="tags" part="tags">
                                  ${product.tags.map((tag) => html`<span class="tag" part="tag">${tag}</span>`)}
                              </div>`
                            : nothing
                    }
                </section>
            </article>
        `;
    }

    private renderComponents(components: readonly CommerceProductComponent[]): TemplateResult {
        return html`
            <ul class="component-list" part="kit-component-list">
                ${components.map(
                    (component) => html`
                        <li>
                            <div class="component" part="kit-component">
                                ${
                                    component.image === null
                                        ? html`<span
                                              class="component-image-placeholder"
                                              aria-hidden="true"
                                          ></span>`
                                        : html`<img
                                              src=${component.image.url}
                                              alt=""
                                              loading="lazy"
                                              decoding="async"
                                          />`
                                }
                                <span class="component-name">${component.name}</span>
                                <span class="component-quantity">${component.quantity} ×</span>
                            </div>
                            ${
                                component.components === undefined ||
                                component.components.length === 0
                                    ? nothing
                                    : this.renderComponents(component.components)
                            }
                        </li>
                    `,
                )}
            </ul>
        `;
    }

    private displayImages(product: CommerceProduct): readonly CommerceImage[] {
        const images: CommerceImage[] = [];
        const seenUrls = new Set<string>();

        for (const image of product.images) {
            const validImage = this.validImage(image);

            if (validImage === null) {
                continue;
            }

            const normalizedUrl = validImage.url.trim();
            const identity = normalizedUrl.toLowerCase();

            if (seenUrls.has(identity)) {
                continue;
            }

            seenUrls.add(identity);
            images.push({
                ...validImage,
                url: normalizedUrl,
            });
        }

        return images;
    }

    private validImage(image: CommerceImage | null | undefined): CommerceImage | null {
        if (image === null || image === undefined || image.url.trim().length === 0) {
            return null;
        }

        try {
            const url = new URL(image.url.trim());

            if (url.protocol !== "https:" && url.protocol !== "http:") {
                return null;
            }
        } catch {
            return null;
        }

        return image;
    }

    private selectRelativeImage(direction: -1 | 1, imageCount: number): void {
        if (imageCount < 2) return;

        this.activeImageIndex = (this.activeImageIndex + direction + imageCount) % imageCount;
    }

    private readonly productImagePointerDown = (event: PointerEvent): void => {
        this.imagePointerStartX = event.clientX;
    };

    private productImagePointerUp(event: PointerEvent, imageCount: number): void {
        const startX = this.imagePointerStartX;
        this.imagePointerStartX = undefined;

        if (startX === undefined || imageCount < 2) return;

        const distance = event.clientX - startX;
        if (Math.abs(distance) < 35) return;

        this.selectRelativeImage(distance > 0 ? -1 : 1, imageCount);
    }

    private readonly cancelProductImageSwipe = (): void => {
        this.imagePointerStartX = undefined;
    };

    private renderDetailSections(product: CommerceProduct): TemplateResult | typeof nothing {
        const sections = product.detailSections ?? [];
        if (sections.length === 0) return nothing;

        return html`<div class="detail-sections" part="detail-sections">
            ${sections.map((section) =>
                section.display === "accordion"
                    ? html`<details class="detail-accordion" part="detail-section detail-accordion">
                          <summary>${section.title ?? "More information"}</summary>
                          ${this.renderDetailContent(section.format, section.content)}
                      </details>`
                    : html`<section class="detail-section" part="detail-section detail-body">
                          ${section.title === undefined ? nothing : html`<h2>${section.title}</h2>`}
                          ${this.renderDetailContent(section.format, section.content)}
                      </section>`,
            )}
        </div>`;
    }

    private renderDetailContent(
        format: "plain-text" | "rich-html",
        content: string,
    ): TemplateResult {
        return format === "rich-html"
            ? html`<div class="detail-rich" part="detail-content rich-content">
                  ${unsafeHTML(sanitizeCommerceHtml(content))}
              </div>`
            : html`<div class="detail-plain" part="detail-content">${content}</div>`;
    }

    private async bindCustomer(): Promise<void> {
        const runtime = await this.requireCommerce();
        this.customerAuthenticated = runtime.customer.isAuthenticated;
        this.unsubscribeCustomer?.();
        this.unsubscribeCustomer = runtime.customer.onChanged(() => {
            this.customerAuthenticated = runtime.customer.isAuthenticated;
            if (!this.customerAuthenticated) this.quickBuyOpen = false;
        });
    }

    private async synchronizeProduct(): Promise<void> {
        const sequence = ++this.loadSequence;
        this.errorMessage = "";

        if (this.product !== undefined) {
            this.loading = false;
            this.applyProduct(this.product);
            return;
        }

        this.loading = true;

        try {
            const runtime = await this.requireCommerce();
            const lookup = this.resolveLookup(runtime, this.identifier, this.identity || undefined);

            if (lookup === null) {
                this.currentProduct = undefined;
                return;
            }

            const product = await runtime.products.get(lookup.identifier, lookup.identity);

            if (sequence !== this.loadSequence) {
                return;
            }

            this.applyProduct(product);
            this.dispatchLoaded(product);
        } catch (error) {
            if (sequence === this.loadSequence) {
                this.currentProduct = undefined;
                this.errorMessage = this.describe(error);
            }
        } finally {
            if (sequence === this.loadSequence) {
                this.loading = false;
            }
        }
    }

    private resolveLookup(
        runtime: Commerce,
        identifier: string,
        identity?: CommerceProductIdentity,
    ): CommerceProductLookup | null {
        const normalizedIdentifier = identifier.trim();

        if (normalizedIdentifier.length > 0) {
            return {
                identifier: normalizedIdentifier,
                identity: identity ?? runtime.configuration.productDetail.identity,
            };
        }

        return runtime.products.resolveDetailLookup();
    }

    private dispatchLoaded(product: CommerceProduct): void {
        this.dispatchEvent(
            new CustomEvent<CommerceProduct>("teqtank-product-loaded", {
                detail: product,
                bubbles: true,
                composed: true,
            }),
        );
    }

    private async requireCommerce(): Promise<Commerce> {
        if (this.commerce !== undefined) {
            return this.commerce;
        }

        this.commerce = await TeqtankCommerce.initialize({
            commerceKey: this.commerceKey,
            dataSource: this.dataSource,
        });
        this.ownsCommerce = true;

        return this.commerce;
    }

    private applyProduct(product: CommerceProduct): void {
        this.currentProduct = product;
        this.quantity = 1;
        this.activeImageIndex = 0;
        this.purchaseType = product.representativeOffers.some(
            (offer) => offer.purchaseType === "recurring",
        )
            ? "recurring"
            : "one-time";

        const defaultVariant =
            product.variants.find((variant) => variant.id === product.defaultVariantId) ??
            product.variants[0];
        const selectedValues: Record<string, string> = {};

        for (const option of product.optionGroups) {
            const preferred = defaultVariant?.selections.find(
                (selection) => selection.optionId === option.id,
            )?.valueId;
            const values = this.availableValues(product, option.id, selectedValues);
            const selected = values.find((value) => value.id === preferred) ?? values[0];

            if (selected !== undefined) {
                selectedValues[option.id] = selected.id;
            }
        }

        this.selectedValues = Object.freeze(selectedValues);
    }

    private resolveSelectedVariant(product: CommerceProduct): CommerceProductVariant | null {
        if (product.variants.length === 0) {
            return null;
        }

        return (
            product.variants.find((variant) => {
                const activeOptionIds = this.activeOptionGroups(product).map((option) => option.id);
                return (
                    variant.selections.length === activeOptionIds.length &&
                    variant.selections.every(
                        (selection) =>
                            this.selectedValues[selection.optionId] === selection.valueId,
                    ) &&
                    activeOptionIds.every((optionId) =>
                        variant.selections.some((selection) => selection.optionId === optionId),
                    )
                );
            }) ?? null
        );
    }

    private selectOption(optionId: string, valueId: string): void {
        const product = this.currentProduct;

        if (product === undefined) {
            return;
        }

        const selectedValues: Record<string, string> = {
            ...this.selectedValues,
            [optionId]: valueId,
        };
        const changedIndex = product.optionGroups.findIndex((option) => option.id === optionId);

        for (let index = changedIndex + 1; index < product.optionGroups.length; index += 1) {
            const option = product.optionGroups[index];

            if (option === undefined) {
                continue;
            }

            const values = this.availableValues(product, option.id, selectedValues);

            if (!values.some((value) => value.id === selectedValues[option.id])) {
                const first = values[0];

                if (first === undefined) {
                    delete selectedValues[option.id];
                } else {
                    selectedValues[option.id] = first.id;
                }
            }
        }

        this.selectedValues = Object.freeze(selectedValues);
        this.errorMessage = "";
    }

    private availableValues(
        product: CommerceProduct,
        optionId: string,
        selections: Readonly<Record<string, string>> = this.selectedValues,
    ) {
        const option = product.optionGroups.find((candidate) => candidate.id === optionId);

        if (option === undefined) {
            return [];
        }

        if (option.dependsOnOptionId === undefined) {
            return option.values;
        }

        const parentValueId = selections[option.dependsOnOptionId];

        if (parentValueId === undefined) {
            return [];
        }

        return option.values.filter(
            (value) => value.availableForValueIds?.includes(parentValueId) === true,
        );
    }

    private activeOptionGroups(
        product: CommerceProduct,
        selections: Readonly<Record<string, string>> = this.selectedValues,
    ) {
        return product.optionGroups.filter(
            (option) => this.availableValues(product, option.id, selections).length > 0,
        );
    }

    private selectPurchaseType(purchaseType: CommerceCartPurchaseType): void {
        this.purchaseType = purchaseType;
        this.errorMessage = "";
    }

    private readonly decreaseQuantity = (): void => {
        this.quantity = Math.max(1, this.quantity - 1);
    };

    private readonly increaseQuantity = (): void => {
        this.quantity = Math.min(99, this.quantity + 1);
    };

    private readonly changeQuantity = (event: Event): void => {
        const input = event.currentTarget as HTMLInputElement;
        const value = Number.parseInt(input.value, 10);
        this.quantity = Number.isFinite(value) ? Math.min(99, Math.max(1, value)) : 1;
    };

    private readonly handleAddToCart = async (): Promise<void> => {
        this.adding = true;
        this.errorMessage = "";

        try {
            await this.addToCart();
        } catch (error) {
            this.errorMessage = this.describe(error);
        } finally {
            this.adding = false;
        }
    };

    private readonly requestClose = (): void => {
        this.dispatchEvent(
            new CustomEvent("teqtank-product-close", {
                bubbles: true,
                composed: true,
            }),
        );
    };

    private describe(error: unknown): string {
        return error instanceof Error ? error.message : "The Product could not be loaded.";
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-product-detail": TeqtankProductDetail;
    }
}
